import { useState, type FormEvent } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DeleteHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

const CONFIRMATION_WORD = 'delete';

export function DeleteHistoryDialog({
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
}: DeleteHistoryDialogProps) {
  const [confirmationInput, setConfirmationInput] = useState('');

  const isConfirmationValid = confirmationInput.toLowerCase() === CONFIRMATION_WORD;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isConfirmationValid || isDeleting) return;
    try {
      await onConfirm();
      setConfirmationInput('');
    } catch {
      // Error handling is delegated to parent via onConfirm's implementation
      // We catch here to prevent unhandled rejection
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setConfirmationInput('');
    }
    onOpenChange(newOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive">
            Delete All Message History
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>
              This action will permanently delete all your conversation history. This includes all
              messages, responses, and associated data.
            </p>
            <p className="font-semibold text-destructive">This action cannot be undone.</p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="confirm-delete">
              Type <span className="font-mono font-bold">{CONFIRMATION_WORD}</span> to confirm
            </Label>
            <Input
              id="confirm-delete"
              type="text"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              placeholder={`Type "${CONFIRMATION_WORD}" to confirm`}
              disabled={isDeleting}
              autoComplete="off"
              autoFocus
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              type="submit"
              variant="destructive"
              disabled={!isConfirmationValid || isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete All History'}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
